import React from "react";
import storyImage from "../assets/img_aboutus.png";
import Reveal from "../components/Reveal";

const directors = [
  {
    name: "Hamit Soyel",
    initials: "HS",
    role: "Director & Head of AI/ML",
    bio: "Hamit leads the AI and machine-learning side of Deep-C Vision, from on-device models to production LLM systems. He also works with founders and research teams on the earlier questions — what to build first, how to frame an MVP so it proves something, and where a product can realistically grow.",
    link: "https://www.linkedin.com/in/hsoyel/",
    linkLabel: "LINKEDIN",
  },
  {
    name: "Zehra Mehmet",
    initials: "ZM",
    role: "Director & Chief Operating Officer",
    bio: "Zehra runs Deep-C Vision's operations, handling the commercial and day-to-day needs that keep projects moving and the studio steady. She also leads growth forecasting, shaping how the company plans ahead and where it takes on work next.",
  },
];

const crew = [
  {
    name: "Vishal Mogal",
    initials: "VM",
    role: "Lead Data Scientist & MLOps",
    bio: "Leads data science and MLOps, taking models from training through to production on AWS.",
  },
  {
    name: "Jakub Barszczewski",
    initials: "JB",
    role: "Full-Stack Consultant (AWS)",
    bio: "Full-stack consultant with deep AWS experience across our backend and release infrastructure.",
    link: "https://www.linkedin.com/in/jakub-barszczewski/",
    linkLabel: "LINKEDIN",
  },
  {
    name: "Vishal Kataria",
    initials: "VK",
    role: "Software Developer (Full-Stack)",
    bio: "Full-stack development across the studio's mobile and web products.",
  },
];

// From Deep-C Vision's own 2025 highlights post — all public.
const collaborations = [
  {
    project: "ReFleks",
    funder: "UK Research and Innovation",
    people: ["Suzanne Eldridge", "Francesco Dell'Accio"],
  },
  {
    project: "HepB&Me",
    funder: "Queen Mary University of London",
    people: ["Patrick Kennedy", "Mark Danta", "ALMuthana Mohamed"],
  },
  {
    project: "TabletopGames.ai",
    funder: "EPSRC",
    people: ["Diego Pérez Liébana", "Raluca Gaina"],
  },
  {
    project: "From Pixels to Populations",
    funder: "EPSRC",
    people: [
      "Silvia Liverani",
      "Kostas Papafitsoros",
      "Eleni Matechou",
      "Linus Wunderlich",
    ],
  },
];

const PersonCard = ({ person, compact = false }) => (
  <article className="surface-card h-full p-8 flex flex-col">
    <div className="flex items-center gap-5 mb-6">
      <span
        aria-hidden="true"
        className={`shrink-0 rounded-2xl bg-brand-grad flex items-center justify-center
                    font-display tracking-wider text-white shadow-glow ${compact ? "w-12 h-12 text-[18px]" : "w-16 h-16 text-[24px]"
          }`}
      >
        {person.initials}
      </span>
      <div>
        <h3
          className={`font-display leading-none tracking-wide text-paper ${compact ? "text-[22px]" : "text-[28px]"
            }`}
        >
          {person.name}
        </h3>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-brand-cyan mt-2">
          {person.role}
        </p>
      </div>
    </div>

    <p className="text-sm text-muted leading-relaxed flex-1">{person.bio}</p>

    {person.link && (
      <a
        href={person.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-6 font-mono text-[10px]
                   tracking-[0.2em] text-muted hover:text-brand-cyan transition-colors"
      >
        {person.linkLabel}
        <span aria-hidden="true">↗</span>
      </a>
    )}
  </article>
);

const AboutUs = () => {
  return (
    <div className="bg-ink pt-28 lg:pt-32">
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-16 pb-20 text-center">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_40%,rgba(91,62,255,0.16)_0%,transparent_70%)]" />
        <div className="pointer-events-none absolute inset-0 hero-grid" />

        <div className="relative z-10 max-w-4xl mx-auto">
          <p className="eyebrow mb-6 fade-up" style={{ animationDelay: "0.2s" }}>
            About us
          </p>
          <h1
            className="font-display text-[clamp(56px,10vw,120px)] leading-[0.92] tracking-wide text-paper fade-up"
            style={{ animationDelay: "0.35s" }}
          >
            A studio that <span className="text-brand-cyan">ships</span>
          </h1>
          <p
            className="mt-7 mx-auto max-w-xl text-muted text-[17px] leading-relaxed fade-up"
            style={{ animationDelay: "0.55s" }}
          >
            A UK product studio that designs, builds and ships its own apps —
            and its clients'.
          </p>
        </div>
      </section>

      {/* Our story */}
      <section className="px-6 sm:px-12 lg:px-20 py-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center">
          <Reveal>
            <p className="eyebrow mb-5">Our story</p>
            <h2 className="display-title mb-6">Design, then code</h2>
            <div className="w-20 h-[3px] rounded-full bg-brand-grad mb-8" />
            <div className="text-body text-muted space-y-5">
              <p>
                Deep-C Vision was born from a passion for purposeful design and
                powerful technology. What started as a vision to simplify
                complex digital experiences has grown into a full-service design
                and development studio trusted by startups and enterprises
                alike.
              </p>
              <p>
                We blend creativity with code to craft seamless solutions from
                intuitive interfaces to scalable mobile platforms. Driven by
                detail, inspired by innovation, and focused on results, we build
                digital products people love to use.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <img
              src={storyImage}
              alt="The Deep-C Vision team"
              className="rounded-tile w-full h-auto object-cover border border-ink-line shadow-card"
            />
          </Reveal>
        </div>
      </section>

      {/* People */}
      <section className="border-t border-ink-line bg-ink-soft px-6 sm:px-12 lg:px-20 py-28">
        <div className="max-w-6xl mx-auto">
          <Reveal className="mb-16">
            <p className="eyebrow mb-5">People</p>
            <h2 className="display-title">Who you'll work with</h2>
            <p className="text-body text-muted max-w-md mt-6">
              A small team — you deal with the people doing the work.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {directors.map((person, i) => (
              <Reveal key={person.name} delay={i * 120}>
                <PersonCard person={person} />
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {crew.map((person, i) => (
              <Reveal key={person.name} delay={i * 100}>
                <PersonCard person={person} compact />
              </Reveal>
            ))}
          </div>

          <Reveal delay={240} className="mt-10">
            <p className="text-sm text-muted">
              Design is delivered with our partner{" "}
              <a
                href="https://understated.me/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand-cyan hover:underline"
              >
                Understated
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* Research collaborations */}
      <section className="border-t border-ink-line px-6 sm:px-12 lg:px-20 py-28">
        <div className="max-w-6xl mx-auto">
          <Reveal className="mb-16">
            <p className="eyebrow mb-5">Research collaborations</p>
            <h2 className="display-title">Turning research into products</h2>
            <p className="text-body text-muted max-w-lg mt-6">
              We work with academic teams to take funded research out of the lab
              and into something people can actually use.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {collaborations.map((c, i) => (
              <Reveal key={c.project} delay={(i % 2) * 110}>
                <article className="surface-card h-full p-8">
                  <h3 className="font-display text-[26px] leading-none tracking-wide text-paper mb-3">
                    {c.project}
                  </h3>
                  <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-brand-cyan mb-6">
                    Funded by {c.funder}
                  </p>
                  <ul className="flex flex-wrap gap-2">
                    {c.people.map((n) => (
                      <li
                        key={n}
                        className="text-[11px] text-muted border border-ink-line rounded-full px-3 py-1.5"
                      >
                        {n}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;
